import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Mail, Save, Info, Server, Send } from "lucide-react";

const empty = {
  from_name: "", reply_to: "", onboarding_enabled: true, reset_enabled: true,
  provider: "managed", smtp_host: "", smtp_port: 587, smtp_user: "",
  smtp_password: "", smtp_from_email: "", smtp_security: "tls",
};

export default function EmailSettings() {
  const [form, setForm] = useState(empty);
  const [pwdSet, setPwdSet] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/settings/email").then((r) => {
      const d = r.data || {};
      setForm({
        from_name: d.from_name || "", reply_to: d.reply_to || "",
        onboarding_enabled: d.onboarding_enabled !== false, reset_enabled: d.reset_enabled !== false,
        provider: d.provider || "managed", smtp_host: d.smtp_host || "", smtp_port: d.smtp_port || 587,
        smtp_user: d.smtp_user || "", smtp_password: "", smtp_from_email: d.smtp_from_email || "",
        smtp_security: d.smtp_security || "tls",
      });
      setPwdSet(!!d.smtp_password_set);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/settings/email", { ...form, smtp_port: parseInt(form.smtp_port) || 587 });
      setPwdSet(!!data.smtp_password_set);
      setForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Email settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter an email to send the test to"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/email/test", { to: testTo });
      toast.success(`Test email sent via ${data.provider === "smtp" ? "your SMTP server" : "managed provider"}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const Toggle = ({ k, label, desc }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <div><div className="text-sm font-medium text-slate-800">{label}</div><div className="text-xs text-slate-400">{desc}</div></div>
      <button type="button" data-testid={`toggle-${k}`} onClick={() => set(k, !form[k])}
        className={`relative w-11 h-6 rounded-full transition-colors ${form[k] ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form[k] ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );

  const isSmtp = form.provider === "smtp";

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Email &amp; Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">Configure how password &amp; notification emails are delivered</p>
      </div>

      <form onSubmit={save} className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm space-y-5">
        <div className="flex items-center gap-3 pb-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><Server size={20} /></div>
          <h3 className="text-base font-semibold text-slate-800 font-heading">Delivery Provider</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" data-testid="provider-managed" onClick={() => set("provider", "managed")}
            className={`text-left p-4 rounded-lg border transition-colors ${!isSmtp ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="text-sm font-semibold text-slate-800">Managed (Default)</div>
            <div className="text-xs text-slate-500 mt-0.5">Emergent secure mailer. No setup needed.</div>
          </button>
          <button type="button" data-testid="provider-smtp" onClick={() => set("provider", "smtp")}
            className={`text-left p-4 rounded-lg border transition-colors ${isSmtp ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="text-sm font-semibold text-slate-800">Custom SMTP</div>
            <div className="text-xs text-slate-500 mt-0.5">Your own server (Gmail, arjunfintech.com…).</div>
          </button>
        </div>

        {isSmtp && (
          <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>SMTP Host *</label><input data-testid="smtp-host-input" required={isSmtp} value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} className={inputCls} placeholder="smtp.gmail.com" /></div>
              <div><label className={labelCls}>Port *</label><input data-testid="smtp-port-input" required={isSmtp} type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} className={inputCls} placeholder="587" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Security</label>
                <select data-testid="smtp-security-select" value={form.smtp_security} onChange={(e) => set("smtp_security", e.target.value)} className={inputCls}>
                  <option value="tls">STARTTLS (587)</option><option value="ssl">SSL/TLS (465)</option><option value="none">None</option>
                </select>
              </div>
              <div><label className={labelCls}>From Email</label><input data-testid="smtp-from-input" type="email" value={form.smtp_from_email} onChange={(e) => set("smtp_from_email", e.target.value)} className={inputCls} placeholder="noreply@arjunfintech.com" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>SMTP Username *</label><input data-testid="smtp-user-input" required={isSmtp} value={form.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} className={inputCls} placeholder="you@gmail.com" /></div>
              <div><label className={labelCls}>SMTP Password {pwdSet && <span className="text-emerald-600 normal-case">· saved</span>}</label><input data-testid="smtp-password-input" type="password" value={form.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} className={inputCls} placeholder={pwdSet ? "•••••••• (leave blank to keep)" : "App password"} /></div>
            </div>
            <p className="text-xs text-slate-500">For Gmail, use an <strong>App Password</strong> (not your account password) with 2-step verification enabled.</p>
          </div>
        )}
        {!isSmtp && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800">
            <Info size={18} className="mt-0.5 flex-shrink-0" />
            <span>Emails go out through Emergent's managed provider — no SMTP host or password needed.</span>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><Mail size={20} /></div>
          <h3 className="text-base font-semibold text-slate-800 font-heading">Sender &amp; Notifications</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Sender Display Name</label><input data-testid="from-name-input" value={form.from_name} onChange={(e) => set("from_name", e.target.value)} className={inputCls} placeholder="Arjun Fintech LLP" /></div>
          <div><label className={labelCls}>Reply-To Email</label><input data-testid="reply-to-input" type="email" value={form.reply_to} onChange={(e) => set("reply_to", e.target.value)} className={inputCls} placeholder="support@arjunfintech.com" /></div>
        </div>
        <div>
          <Toggle k="onboarding_enabled" label="Partner onboarding email" desc="Set-password link sent when a partner is approved" />
          <Toggle k="reset_enabled" label="Password reset email" desc="Reset link sent from the Forgot Password screen" />
        </div>
        <div className="flex justify-end">
          <button data-testid="save-email-settings-button" type="submit" className={btnPrimary}><Save size={16} /> Save Settings</button>
        </div>
      </form>

      <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm">
        <div className="text-sm font-semibold text-slate-800 mb-1">Send a test email</div>
        <div className="text-xs text-slate-500 mb-3">Verify your configuration by sending a test message. Save your settings first.</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="test-email-input" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className={inputCls} placeholder="recipient@example.com" />
          <button data-testid="send-test-email-button" type="button" onClick={sendTest} disabled={testing} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"><Send size={15} /> {testing ? "Sending…" : "Send Test"}</button>
        </div>
      </div>
    </div>
  );
}
